import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface DataMapping {
  id?: string;
  userId: number;
  organizationId: number;
  inputCode: string;
  mappingType: 'whole' | 'percent';
  percentValue?: number;
  kpiCode: string;
  createdAt?: string;
  updatedAt?: string;
  isActive: boolean;
}

export interface CreateMappingRequest {
  inputCode: string;
  mappingType: 'whole' | 'percent';
  percentValue?: number;
  kpiCode: string;
}

export interface BulkMappingRequest {
  mappings: CreateMappingRequest[];
}

export interface AIMappingSuggestion {
  inputCode: string;
  suggestedKpiCode: string;
  confidence: number;
  reasoning: string;
}

export interface AIMappingRequest {
  inputData: Array<{
    identifier: string, 
    value: string,
    allColumns?: { [columnName: string]: string }
  }>;
  kpiCategories: Array<{
    id: number;
    name: string;
    kpis: Array<{code: string, name: string, description: string}>;
  }>;
}

export interface AIMappingResponse {
  success: boolean;
  data: {
    suggestedMappings: AIMappingSuggestion[];
    totalProcessed: number;
    totalMapped: number;
  };
  message?: string;
}

class DataMappingService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  }

  // Get all data mappings for the current user's organization
  async getDataMappings(inputCode?: string): Promise<DataMapping[]> {
    try {
      const url = inputCode 
        ? `${API_BASE_URL}/data-mappings?input_code=${encodeURIComponent(inputCode)}`
        : `${API_BASE_URL}/data-mappings`;
        
      const response = await axios.get(url, this.getAuthHeaders());
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch data mappings');
      }
    } catch (error: any) {
      console.error('Error fetching data mappings:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch data mappings');
    }
  }

  // Get a specific data mapping by ID
  async getDataMappingById(id: string): Promise<DataMapping> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/data-mappings/${id}`, 
        this.getAuthHeaders()
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch data mapping');
      }
    } catch (error: any) {
      console.error('Error fetching data mapping:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch data mapping');
    }
  }

  // Create a new data mapping
  async createDataMapping(mapping: CreateMappingRequest): Promise<DataMapping> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/data-mappings`, 
        mapping,
        this.getAuthHeaders()
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create data mapping');
      }
    } catch (error: any) {
      console.error('Error creating data mapping:', error);
      throw new Error(error.response?.data?.message || 'Failed to create data mapping');
    }
  }

  // Update a data mapping
  async updateDataMapping(id: string, updates: Partial<CreateMappingRequest>): Promise<DataMapping> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/data-mappings/${id}`, 
        updates,
        this.getAuthHeaders()
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update data mapping');
      }
    } catch (error: any) {
      console.error('Error updating data mapping:', error);
      throw new Error(error.response?.data?.message || 'Failed to update data mapping');
    }
  }

  // Delete a data mapping
  async deleteDataMapping(id: string): Promise<void> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/data-mappings/${id}`, 
        this.getAuthHeaders()
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete data mapping');
      }
    } catch (error: any) {
      console.error('Error deleting data mapping:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete data mapping');
    }
  }

  // Create multiple mappings at once
  async createBulkMappings(request: BulkMappingRequest): Promise<DataMapping[]> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/data-mappings/bulk`, 
        request,
        this.getAuthHeaders()
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create bulk mappings');
      }
    } catch (error: any) {
      console.error('Error creating bulk mappings:', error);
      throw new Error(error.response?.data?.message || 'Failed to create bulk mappings');
    }
  }

  // Delete multiple mappings
  async deleteBulkMappings(ids: string[]): Promise<void> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/data-mappings/bulk`, 
        {
          ...this.getAuthHeaders(),
          data: { ids }
        }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete bulk mappings');
      }
    } catch (error: any) {
      console.error('Error deleting bulk mappings:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete bulk mappings');
    }
  }

  // Generate AI mapping suggestions
  async getAIMappingSuggestions(request: AIMappingRequest): Promise<AIMappingResponse> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/data-mappings/ai-suggest`,
        request,
        this.getAuthHeaders()
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error getting AI mapping suggestions:', error);
      throw new Error(error.response?.data?.message || 'Failed to get AI mapping suggestions');
    }
  }
}

export const dataMappingService = new DataMappingService();